/*
 * CONTACT LIST COMPONENT
 * 
 * Displays user's personal contact list in a responsive card grid layout.
 * 
 * Key Features:
 * - Fetches and displays contacts assigned to the current user
 * - Responsive grid layout: 4→2→1 columns (skipping 3-column layout)
 * - Real-time search filtering across contact fields
 * - Team-based access control with loading states
 * - Contact removal functionality from user's list
 * - Chat-aware responsive adjustments
 * - Distinct saved-collection and search empty states with direct recovery actions
 * 
 * Responsive Grid Behavior:
 * - Desktop (lg+): 4 columns → 2 columns (with chat)
 * - Tablet (md): 2 columns → 2 columns (with chat)
 * - Mobile (sm): 2 columns → 1 column (with chat)
 * - Mobile (base): 1 column
 * 
 * Changes:
 * - Updated to use 'my-contacts' component type for 4→2→1 responsive pattern
 * - Skips 3-column layout for cleaner responsive transitions
 * - 2026-08-06: Replaced local fetch/removal state with TanStack Query
 */

import { FC, useEffect } from 'react';
import { THEME_CLASSES } from '../../constants/themeConstants';
import ContactCard from './ContactCard';
import { useChatContext } from '../../context/ChatContext';
import Spinner from '../common/Spinner';
import { filterContacts, getContactDisplayFields } from '../../utils/contactUtils';
import { useTeams } from '../../context/TeamContext';
import { getResponsiveGridClassesWithTransitions } from '../../utils/responsiveUtils';
import { useRemoveUserContactMutation, useUserContactsQuery } from '../../query/contactQueries';
import { Button } from '../primitives';

interface ContactListProps {
  searchValue: string;
  onRowCountChange?: (count: number) => void;
  onBrowseAllContacts: () => void;
  onClearSearch: () => void;
}

const ContactList: FC<ContactListProps> = ({
  searchValue,
  onRowCountChange,
  onBrowseAllContacts,
  onClearSearch,
}) => {
  const { currentUserId, isLoading } = useTeams();
  const { isChatVisible } = useChatContext();
  const contactsQuery = useUserContactsQuery(currentUserId);
  const removeContact = useRemoveUserContactMutation();
  const contacts = contactsQuery.data ?? [];
  const loading = Boolean(currentUserId && contactsQuery.isPending);
  const error = contactsQuery.error instanceof Error
    ? contactsQuery.error.message
    : contactsQuery.error ? 'Failed to load contacts' : null;
  const keywords = searchValue.toLowerCase().split(/\s+/).filter(Boolean);

  const searchFields = getContactDisplayFields().map((field: any) => field.key);
  const filteredContacts = filterContacts(contacts, searchValue, searchFields);

  // Row count updates
  useEffect(() => {
    if (onRowCountChange) {
      onRowCountChange(filteredContacts.length);
    }
  }, [filteredContacts.length, onRowCountChange]);

  // Custom handler to remove contacts from user's contact list
  const handleDeleteContact = async (id: number) => {
    if (!currentUserId) return;

    try {
      await removeContact.mutateAsync({
        userId: currentUserId,
        id: id
      });
    } catch (err) {
      console.error('Error removing contact from user list:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
        <span className="ml-3">Loading user data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center h-64 flex items-center justify-center">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  // Calculate responsive grid classes based on chat visibility
  const gridClasses = getResponsiveGridClassesWithTransitions('my-contacts', isChatVisible);

  return (
    <>
      {filteredContacts.length === 0 ? (
        <div className={`flex h-64 flex-col items-center justify-center gap-3 px-6 text-center ${THEME_CLASSES.text.secondary}`}>
          <h2 className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>
            {searchValue ? 'No contacts match your search' : 'No saved contacts yet'}
          </h2>
          <p className="max-w-md text-sm">
            {searchValue
              ? 'Try another term or clear the current search.'
              : 'Browse all contacts to find the people you work with most often.'}
          </p>
          <Button variant="secondary" size="sm" onClick={searchValue ? onClearSearch : onBrowseAllContacts}>
            {searchValue ? 'Clear search' : 'Browse all contacts'}
          </Button>
        </div>
      ) : (
        <div className={`${gridClasses} mt-4`}>
          {filteredContacts.map((contact) => (
            <div key={contact.id}>
              <ContactCard
                contact={contact}
                keywords={keywords}
                onDelete={handleDeleteContact}
                showDeleteButton={true}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ContactList;
